/**
 * Flor de Damas — scroll-scrubbed frame sequence.
 *
 * Apple-style pinned canvas: pre-extracted WebP frames are preloaded and the
 * one matching the current scroll position is drawn to a <canvas>. The stage
 * is pinned with CSS `position: sticky`; this file only reads scroll position
 * (rAF-throttled) and paints — no scroll library, matching the theme's
 * zero-dependency architecture.
 *
 * Degrades cleanly: JS off or `prefers-reduced-motion` -> the static poster
 * stays visible and nothing here runs.
 */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const BG_FALLBACK = '#0A0B0C';

  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

  function initScrollSequence(root) {
    if (root.dataset.seqReady === 'true') return;
    root.dataset.seqReady = 'true';

    const canvas = root.querySelector('[data-seq-canvas]');
    const ctx = canvas && canvas.getContext('2d', { alpha: false });
    const track = root.querySelector('[data-seq-track]');
    const poster = root.querySelector('[data-seq-poster]');
    const overlay = root.querySelector('[data-seq-overlay]');
    if (!canvas || !ctx || !track) return;

    // Reduced motion: leave the poster, never load frames or pin.
    if (reducedMotion.matches) {
      root.removeAttribute('data-loading');
      canvas.style.display = 'none';
      if (overlay) overlay.style.setProperty('--seq-overlay', '1');
      return;
    }

    // Pick the frame set once. Mobile (~1MB) vs desktop (~3MB).
    const isMobile = mobileQuery.matches;
    const listEl = isMobile
      ? root.querySelector('[data-seq-frames-mobile]')
      : root.querySelector('[data-seq-frames-desktop]');

    let urls;
    try {
      urls = JSON.parse(listEl.textContent);
    } catch (error) {
      return; // markup malformed — poster stays, page is fine.
    }
    const frameCount = urls.length;
    if (!frameCount) return;

    const scrollLength = parseFloat(root.dataset.scrollLength) || 3;
    const revealAt = (parseFloat(root.dataset.revealAt) || 0) / 100;
    const bg = (getComputedStyle(root).getPropertyValue('--seq-bg') || '').trim() || BG_FALLBACK;

    const images = new Array(frameCount);
    let loaded = 0;
    let started = false;
    let currentFrame = -1;
    let rafId = 0;

    /* ---- drawing -------------------------------------------------- */

    function drawFrame(index) {
      const img = images[index];
      if (!img || !img.complete || !img.naturalWidth) return;
      const cw = canvas.width;
      const ch = canvas.height;

      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, cw, ch);

      // "contain" so the bag is never cropped; black bars stay black.
      const ir = img.naturalWidth / img.naturalHeight;
      const cr = cw / ch;
      let dw;
      let dh;
      if (cr > ir) {
        dh = ch;
        dw = ch * ir;
      } else {
        dw = cw;
        dh = cw / ir;
      }
      // Mobile uses the bag-centered portrait crop, so centre it. On desktop
      // the bag sits in the left third of the wide frame — left-align it so it
      // stays left (not pushed to centre by letterboxing) and the overlay copy
      // owns the blank space on the right.
      const dx = isMobile ? (cw - dw) / 2 : 0;
      ctx.drawImage(img, dx, (ch - dh) / 2, dw, dh);
    }

    function resize() {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
      currentFrame = -1; // force a repaint at the new size
      render();
    }

    /* ---- scroll -> frame ----------------------------------------- */

    function progress() {
      // Distance the stage stays pinned, in px.
      const scrubDistance = window.innerHeight * scrollLength;
      if (scrubDistance <= 0) return 0;
      const top = track.getBoundingClientRect().top;
      return clamp(-top / scrubDistance, 0, 1);
    }

    function render() {
      const p = progress();
      const frame = Math.round(p * (frameCount - 1));

      if (frame !== currentFrame) {
        currentFrame = frame;
        drawFrame(frame);
      }

      if (overlay) {
        // Fade in after the reveal point, hold, fade out before the end.
        let o;
        if (p < revealAt) {
          o = 0;
        } else if (p > 0.86) {
          o = clamp((1 - p) / 0.14, 0, 1);
        } else {
          o = clamp((p - revealAt) / 0.12, 0, 1);
        }
        overlay.style.setProperty('--seq-overlay', o.toFixed(3));
      }
    }

    function onScroll() {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        render();
      });
    }

    function start() {
      if (started) return;
      started = true;
      root.removeAttribute('data-loading');
      root.setAttribute('data-ready', '');
      resize();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', resize);
      render();
    }

    /* ---- preload (lazy) ------------------------------------------ */

    function preload() {
      urls.forEach((url, i) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => {
          loaded += 1;
          if (i === 0 && !started) {
            // Paint the first frame as soon as it exists.
            resize();
          }
          // Reveal once a small buffer is ready so scrubbing has frames.
          if (!started && loaded >= Math.min(frameCount, 8)) start();
        };
        img.onerror = () => {
          loaded += 1;
          if (!started && loaded >= Math.min(frameCount, 8)) start();
        };
        img.src = url;
        images[i] = img;
      });
    }

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries, obs) => {
          if (entries.some((e) => e.isIntersecting)) {
            obs.disconnect();
            preload();
          }
        },
        { rootMargin: '100% 0px' }
      );
      io.observe(root);
    } else {
      preload();
    }
  }

  const boot = () => document.querySelectorAll('[data-scroll-seq]').forEach(initScrollSequence);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when re-rendered inside the theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
